import { blueprintData } from './blueprint.data';
import { BlueprintInstanceType } from '../shared.enums';
import { BlueprintMetadata } from './blueprint-metadata.class';
import { executeToolCfn } from './functions/execute-tool.cfn';
import { getDesignContextCfn } from './functions/get-design-context.cfn';
import { getToolDefinitionsCfn } from './functions/get-tool-definitions.cfn';
import { Kitchens } from '../kitchens/kitchens.class';

import { BlueprintBase, BlueprintOptions } from '../blueprint-base';
import { BlueprintDataIn, BlueprintDataOut, DesignContext } from './types';
import { ToolDefinition, ToolResult } from './types/tool-definition.interface';

export class Blueprint extends BlueprintBase<BlueprintDataIn> {
	#email: string;
	#schemaVersion: number;
	#submissionDate: string;
	#submissionId: string | null;
	#thumbnail: string;
	#metadata: BlueprintMetadata;
	#kitchens: Kitchens;
	#seededIdentifiers = false;

	constructor (options: BlueprintOptions<BlueprintDataIn> = {}) {
		super(BlueprintInstanceType.Blueprint, { eventGroup: 'blueprint', ...options });

		const data = blueprintData(this.context, options.dataIn);

		this._id = data.id;
		this.#email = data.email;
		this.#schemaVersion = data.schemaVersion;
		this.#submissionDate = data.submissionDate;
		this.#submissionId = data.submissionId;
		this.#thumbnail = data.thumbnail;
		this.#metadata = new BlueprintMetadata({ dataIn: data.metadata });
		this.#kitchens = new Kitchens(this.context, { dataIn: data.kitchens });
		this.seedIdentifierMappings();
		this.setInitialized();
	}

	// exposed private variables
	get schemaVersion (): number { return this.#schemaVersion }
	get email (): string { return this.#email }
	get submissionDate (): string { return this.#submissionDate }
	get submissionId (): string | null { return this.#submissionId }
	get thumbnail (): string { return this.#thumbnail }
	set thumbnail (value: string) { this.#thumbnail = value; }
	get metadata (): BlueprintMetadata { return this.#metadata }
	get kitchens (): Kitchens { return this.#kitchens }

	// toJSON
	public serialize (): BlueprintDataOut {
		return {
			id: this.id,
			email: this.#email,
			submissionDate: this.#submissionDate,
			submissionId: this.#submissionId,
			schemaVersion: this.schemaVersion,
			thumbnail: this.#thumbnail,
			metadata: this.#metadata.serialize(),
			kitchens: this.#kitchens.serialize()
		};
	}

	// eslint-disable-next-line class-methods-use-this
	public getToolDefinitions (): ToolDefinition[] {
		return getToolDefinitionsCfn();
	}

	static getToolDefinitions (): ToolDefinition[] {
		return getToolDefinitionsCfn();
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public executeTool (name: string, args: any): Promise<ToolResult> {
		return executeToolCfn(this, name, args);
	}

	public getDesignContext (): DesignContext {
		return getDesignContextCfn(this);
	}

	private seedIdentifierMappings (): void {
		if (this.#seededIdentifiers) return;
		const { identifierManager: manager } = this.context;
		const kitchen = this.#kitchens?.children?.[0] ?? null;
		if (!kitchen?.design?.islands?.children) {
			this.#seededIdentifiers = true;
			return;
		}
		for (const island of kitchen.design.islands.children) {
			const islandIdentifier = manager.register('islands', island.id);
			if (island.panels?.children) {
				const panelScope = `panels:${island.id}`;
				for (const panel of island.panels.children) {
					manager.register(panelScope, panel.id);
					const applianceScope = `appliances:${island.id}:${panel.id}`;
					if (panel.panelItemGroups?.children) {
						// eslint-disable-next-line max-depth
						for (const group of panel.panelItemGroups.children) {
							// eslint-disable-next-line max-depth
							if (group.items?.children) {
								// eslint-disable-next-line max-depth
								for (const item of group.items.children) {
									manager.register(applianceScope, item.id);
								}
							}
						}
					}
				}
			}
			// eslint-disable-next-line no-void
			void islandIdentifier;
		}
		this.#seededIdentifiers = true;
	}
}
